/**
 * Default roles/permissions/dashboards.
 * Used as initial state and for "Reset to defaults".
 * Edit here to change app defaults (new instances or reset).
 */
export const defaultAccountTypes = [
  {
    text: "internal",
    value: 4,
    desc: "Full visibility, like a manager but does not need to have views set.",
    color: "black",
    defaultViewPermissions: { create: true, edit: true },
    badge: {
      light: { color: "indigo", textColor: "white", icon: "mdi-shield-key" },
      dark: {
        color: "deep-purple lighten-1",
        textColor: "white",
        icon: "mdi-shield-key",
      },
    },
  },
  {
    text: "manager",
    value: 3,
    desc: "Has more permissions than a standard user, can approve orders/submit payments/etc.",
    color: "green",
    defaultViewPermissions: { create: true, edit: false },
    badge: {
      light: { color: "blue", textColor: "white", icon: "mdi-account-tie" },
      dark: {
        color: "blue darken-4",
        textColor: "white",
        icon: "mdi-account-tie",
      },
    },
  },
  {
    text: "user",
    value: 2,
    desc: "Standard user, will need to request manager to approve orders/etc.",
    color: "blue",
    defaultViewPermissions: { create: true, edit: false, exportFile: false },
    badge: {
      light: {
        color: "blue lighten-3",
        textColor: "black",
        icon: "mdi-account",
      },
      dark: { color: "light-blue", textColor: "white", icon: "mdi-account" },
    },
  },
  {
    text: "vendor",
    value: 1,
    desc: "3rd party given access to your MyTrace instance. Account search bar & most features will be hidden/disabled.",
    color: "grey",
    defaultViewPermissions: { create: false, edit: false },
    badge: {
      light: {
        color: "grey lighten-2",
        textColor: "grey darken-2",
        icon: "mdi-handshake",
      },
      dark: {
        color: "grey darken-3",
        textColor: "grey lighten-2",
        icon: "mdi-handshake",
      },
    },
  },
];

export const defaultMytracePermissions = [
  {
    text: "Brand - Show all active",
    desc: "Show all active brands to this user",
    value: "allBrands",
  },
  {
    text: "Brand - Show all related countries",
    desc: "Allow this user to see all countries of the selected brand (user's only see their assigned country by default)",
    value: "allCountries",
  },
  {
    text: "Bypass Order - Manually add materials to location",
    desc: "Allows the user to manually add raw materials to a location. Normally only available for admin users.",
    value: "rawMaterials",
  },
  {
    text: "Set special pricing on a account",
    desc: "Only manager+ users can set special pricing (Product is regularly $14, account gets special deal for $12)",
    value: "specialAccountPricing",
  },
  {
    text: "Edit sales order line items",
    desc: "Only manager+ users can edit order line items once the status is >= accepted",
    value: "lineItems",
  },
  {
    text: "Add payments to sales orders",
    desc: "Only manager+ or users with the 'accounting' role can add payments",
    value: "payments",
  },
  {
    text: "Export data",
    desc: "By default, only manager+ can export data from mytrace.",
    value: "exportData",
  },
  {
    text: "Correct Work Order",
    desc: "Allow editing of components, raw materials, and testing info on work orders past the production phase. All corrections are ledger-tracked and require a reason.",
    value: "correctWorkOrder",
  },
];

export const defaultDepartments = [
  { text: "Administration", value: "administration", sort: 1 },
  { text: "Support", value: "support", sort: 2 },
  { text: "IT/Tech", value: "tech", sort: 3 },
  { text: "Accounting/Finances", value: "accounting", sort: 4 },
  { text: "Legal/Compliance", value: "legal", sort: 5 },
  { text: "Manufacturing", value: "manufacturing", sort: 6 },
  { text: "Logistics/Delivery", value: "delivery", sort: 7 },
  { text: "Sales", value: "sales", sort: 8 },
  { text: "Graphic Design/Creative", value: "creative", sort: 9 },
  { text: "Marketing", value: "marketing", sort: 10 },
];

/**
 * Build a rolePermissions document block for a given account type.
 * @param {string} accountTypeLabel - "vendor" | "user" | "manager" | "admin"
 * @param {object} opts - { views, dashboards, reports, permissions, leaderboard, filterLocation, department, viewPermissionsOverride }
 */
function buildBlock(accountTypeLabel, opts = {}) {
  const {
    views = [],
    dashboards = [],
    reports = [],
    permissions = [],
    leaderboard = false,
    filterLocation = false,
    department = null,
    viewPermissionsOverride = {},
  } = opts;

  // vendor gets empty block (no access by default)
  if (accountTypeLabel === "vendor") {
    const empty = {
      views: [],
      viewPermissions: {},
      dashboards: [],
      reports: [],
      permissions: [],
      leaderboard: false,
      filterLocation: false,
      department: null,
    };
    return { config: empty, custom: JSON.parse(JSON.stringify(empty)) };
  }

  // internal gets empty block — permissions are inferred from accountType, not stored
  if (accountTypeLabel === "internal") {
    const empty = {
      views: [],
      viewPermissions: {},
      dashboards: [],
      reports: [],
      permissions: [],
      leaderboard: false,
      filterLocation: false,
      department: null,
    };
    return { config: empty, custom: JSON.parse(JSON.stringify(empty)) };
  }

  // create/edit/export defaults per account type
  const create = accountTypeLabel === "manager" || accountTypeLabel === "user";
  const edit = accountTypeLabel === "manager";
  const exportFile = accountTypeLabel === "manager";

  const viewPermissions = views.reduce((acc, v) => {
    acc[v] = viewPermissionsOverride[v] || { create, edit, exportFile };
    return acc;
  }, {});

  const config = {
    views: [...views],
    viewPermissions,
    dashboards: [...dashboards],
    reports: [...reports],
    permissions: [...permissions],
    leaderboard,
    filterLocation,
    department,
  };
  return { config, custom: JSON.parse(JSON.stringify(config)) };
}

/**
 * Build a full rolePermissions document (all 4 account type blocks) from a role definition.
 * @param {object} role - { value, text, views, dashboards, reports, permissions, leaderboard, filterLocation, department }
 */
export function buildRoleDoc(role) {
  const {
    value,
    text,
    desc = "",
    views = [],
    dashboards = [],
    reports = [],
    permissions = [],
    leaderboard = false,
    filterLocation = false,
    department = null,
    viewPermissionsOverride = {},
  } = role;
  const doc = { value, text, desc, active: true };
  if (value === "admin") {
    doc.internal = buildBlock("internal", {});
  } else {
    doc.user = buildBlock("user", {
      views,
      dashboards,
      reports,
      permissions,
      leaderboard,
      filterLocation,
      department,
      viewPermissionsOverride,
    });
    doc.manager = buildBlock("manager", {
      views,
      dashboards,
      reports,
      permissions,
      leaderboard,
      filterLocation,
      department,
      viewPermissionsOverride,
    });
  }
  return doc;
}

/**
 * Source role definitions used to bootstrap defaultUserRoles and seed Firestore.
 * Only the user/manager block fields are defined here — vendor/admin are always empty.
 */
const roleDefinitions = [
  {
    value: "accountExecutive",
    text: "Account Executive",
    desc: "Manages Accounts, creates orders & supports growth with trainings/store events",
    views: [
      "tasks",
      "parentAccounts",
      "accounts",
      "account-onboarding",
      "draftOrders",
      "salesOrders",
      "deliveredOrders",
      "events-overview",
      "budtender-training",
      "reports-overview",
      "salesContacts",
    ],
    dashboards: ["sales rep"],
    reports: [],
    permissions: [],
    leaderboard: true,
    department: "sales",
  },
  {
    value: "accountManager",
    text: "Account Manager",
    desc: "Manages Account Executives, Sales",
    views: [
      "tasks",
      "parentAccounts",
      "accounts",
      "account-onboarding",
      "draftOrders",
      "salesOrders",
      "deliveredOrders",
      "events-overview",
      "budtender-training",
      "reports-overview",
      "salesContacts",
    ],
    dashboards: ["sales manager"],
    reports: [],
    permissions: [],
    leaderboard: true,
    department: "sales",
  },
  {
    value: "brandAmbassador",
    text: "Brand Ambassador",
    desc: "Attend store events & educate customers in-store",
    views: ["tasks", "accounts", "events-overview", "budtender-training"],
    dashboards: ["brand ambassador"],
    reports: [],
    permissions: [],
    leaderboard: true,
    department: "support",
  },
  {
    value: "eventManager",
    text: "Event Manager",
    desc: "Manage Store Events & manage Brand Ambassadors",
    views: [
      "tasks",
      "projects",
      "expenses",
      "accounts",
      "events-overview",
      "reports-overview",
      "qrCodes",
      "specials",
      "account-onboarding",
      "promotions-overview",
      "budtender-training",
      "menus",
    ],
    dashboards: ["events"],
    reports: [],
    permissions: [],
    leaderboard: false,
    department: "support",
  },
  {
    value: "ticketRep",
    text: "Ticket Rep",
    desc: "Handle support tickets & customer inquiries",
    views: ["tasks", "accounts", "supportTickets-overview", "supportContacts"],
    dashboards: [],
    reports: [],
    permissions: [],
    leaderboard: false,
    department: "support",
  },
  {
    value: "ticketManager",
    text: "Ticket Manager",
    desc: "Manage Support Tickets, handle returns/defects from Accounts & misc support",
    views: [
      "tasks",
      "accounts",
      "salesOrders",
      "events-overview",
      "supportTickets-overview",
      "reports-overview",
      "supportContacts",
    ],
    dashboards: [],
    reports: [],
    permissions: [],
    leaderboard: false,
    department: "support",
  },
  {
    value: "inventoryManager",
    text: "Inventory Manager",
    desc: "Manages location inventory & order fulfillment",
    views: [
      "tasks",
      "locations",
      "testingLabs",
      "rawMaterialVendors",
      "rawMaterialOrders",
      "draftOrders",
      "salesOrders",
      "deliveredOrders",
      "reports-overview",
    ],
    dashboards: ["fulfillment"],
    reports: [],
    permissions: ["lineItems"],
    leaderboard: false,
    department: "manufacturing",
  },
  {
    value: "workOrders",
    text: "Manufacturing/Work Orders",
    desc: "Manages work orders & manufacturing products",
    views: [
      "tasks",
      "locations",
      "rawMaterialOrders",
      "workOrders",
      "productLots",
      "rawMaterials",
      "testingLabs",
      "productRecipes",
    ],
    dashboards: ["manufacturing"],
    reports: [],
    permissions: [],
    leaderboard: false,
    department: "manufacturing",
  },
  {
    value: "accounting",
    text: "Accounting",
    desc: "Apply payments/credits, Create AR reports & manage finances",
    views: [
      "tasks",
      "vendorOrders",
      "customProducts",
      "customOrders",
      "rawMaterialOrders",
      "parentAccounts",
      "accounts",
      "salesOrders",
      "deliveredOrders",
      "promotions-overview",
      "reports-overview",
      "licenseeSales",
      "payments",
    ],
    dashboards: [],
    reports: [],
    permissions: [],
    leaderboard: false,
    department: "accounting",
  },
  {
    value: "callCenterRepresentative",
    text: "Call Center Representative",
    desc: "Handle incoming calls from customers & support sales orders",
    views: [
      "tasks",
      "parentAccounts",
      "accounts",
      "draftOrders",
      "salesOrders",
      "deliveredOrders",
      "events-overview",
      "reports-overview",
      "budtender-training",
      "account-onboarding",
      "specials",
      "menus",
      "salesContacts",
      "projects",
    ],
    viewPermissionsOverride: {
      specials: { create: false, edit: false, exportFile: false },
      menus: { create: false, edit: false, exportFile: false },
    },
    dashboards: ["lead prospector"],
    reports: [],
    permissions: [],
    leaderboard: true,
    department: "support",
  },
  {
    value: "driver",
    text: "Driver",
    desc: "View assigned delivery routes & manage order deliveries",
    views: ["tasks"],
    dashboards: ["driver"],
    reports: [],
    permissions: [],
    leaderboard: false,
    filterLocation: true,
    department: "delivery",
  },
  {
    value: "admin",
    text: "Admin",
    desc: "Full system administrator",
    views: [],
    dashboards: [],
    reports: [],
    permissions: [],
    leaderboard: false,
    department: "administration",
  },
];

/** Full rolePermissions documents for all 10 default roles. Used for Firestore seeding. */
export const defaultUserRoles = roleDefinitions.map(buildRoleDoc);

/** Custom view entries for presets/roles. If empty, app uses getPhases view list. */
export const defaultAssignableViews = [];

export const defaultDashboards = [
  {
    text: "Vendor Orders",
    desc: "Manage all active vendor orders, grouped by state",
    value: "vendor orders",
  },
  {
    text: "Manufacturing",
    desc: "View incoming raw material / active work orders by selected location",
    value: "manufacturing",
  },
  {
    text: "Fulfillment",
    desc: "Shows in-progress orders & updates in real-time as changes are made",
    value: "fulfillment",
  },
  {
    text: "Sales Rep",
    desc: "View managed accounts/orders/store events/etc to a sales rep. Shows MTD orders vs. goal, current accounts receivable...",
    value: "sales rep",
  },
  {
    text: "Lead Prospector",
    desc: "Similar to Sales Rep dashboard, but focused on leads*",
    value: "lead prospector",
  },
  {
    text: "Sales Manager",
    desc: "View the details of a sales rep's dashboard. Includes option to set goals for all reps in the selected state",
    value: "sales manager",
  },
  {
    text: "Brand Ambassador",
    desc: "Filters active store events assigned to the related user",
    value: "brand ambassador",
  },
  {
    text: "Event Manager",
    desc: "View all store events in the user's associated state. Filter by events where 'Approval Required' & finalize events in 'Ready for Review'",
    value: "events",
  },
  {
    text: "Projects",
    desc: "View active projects associated with this user. Includes assigned tasks",
    value: "projects",
  },
  {
    text: "Credit Approvals",
    desc: "View & approve credit requests for a selected brand/state",
    value: "credit approval",
  },
  {
    text: "Driver",
    desc: "View assigned delivery routes based on selected day. Includes option to clock in/out",
    value: "driver",
  },
  {
    text: "Operations",
    desc: "The operations dashboard consolidates administrative tasks like setting up company info/products and a 2nd mode to manage all states at a glance",
    value: "operations",
  },
  {
    text: "Sales Rep Lite",
    desc: "View managed accounts, orders, and store events, and place orders on behalf of an account.",
    value: "sales rep lite",
  },
];

/** Deep-cloned defaults for reset and initial state. */
export function getDefaultUserSettings() {
  return {
    accountTypes: JSON.parse(JSON.stringify(defaultAccountTypes)),
    mytracePermissions: JSON.parse(JSON.stringify(defaultMytracePermissions)),
    departments: JSON.parse(JSON.stringify(defaultDepartments)),
    userRoles: JSON.parse(JSON.stringify(defaultUserRoles)),
    dashboards: JSON.parse(JSON.stringify(defaultDashboards)),
    assignableViews: JSON.parse(JSON.stringify(defaultAssignableViews)),
  };
}
